import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  Tooltip,
  useTheme,
} from "@mui/material";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckIcon from "@mui/icons-material/Check";
import HtmlRenderer from "./HtmlRenderer";

const MockMcq = ({
  mcqData = {},
  selectedValue = "",
  handleAnswerChange,
  instructions = "",
  sectionTitle = "",
  questionNumber = null,
  asDropdown = false, // NEW PROP
  dropdownContainer = null, // NEW PROP for container reference
  forceMobile = false, // NEW PROP: parent explicitly signals mobile layout
}) => {
  const theme = useTheme();
  const [detectedMobile, setDetectedMobile] = useState(false);

  // Detect mobile via userAgent + width. Combined with forceMobile prop below.
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isMobileDevice = mobileRegex.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      setDetectedMobile(isMobileDevice || (isSmallScreen && isTouchDevice));
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Trust the parent if it explicitly asked for the mobile layout (e.g. from a
  // useMediaQuery breakpoint in ReadingSection), otherwise fall back to UA/width.
  const isMobile = forceMobile || detectedMobile;

  // Which option's "tap the circle" hint tooltip is currently visible.
  const [hintIdx, setHintIdx] = useState(null);
  const hintTimerRef = useRef(null);
  const flashHint = (idx) => {
    setHintIdx(idx);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => setHintIdx(null), 1800);
  };
  useEffect(
    () => () => hintTimerRef.current && clearTimeout(hintTimerRef.current),
    [],
  );

  const blue = theme.palette.primary.main;
  const red = "#b81e33";
  const green = "#0AAF36";
  const greyBg = theme.palette.action.hover;
  const border = theme.palette.divider;

  const { question_id, questionId, question = "", options = [] } = mcqData;
  const id = questionId ?? question_id;
  const displayNumber = questionNumber ?? id ?? "?";

  const letterOfIdx = (i) => String.fromCharCode(65 + i);
  const optionText = (opt) => opt?.text ?? opt?.value ?? opt?.label ?? opt;
  const optionValue = (opt, idx) => opt?.key ?? letterOfIdx(idx);

  return (
    <Box sx={{ width: "100%" }}>
      {Boolean(instructions) && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            border: `1px solid ${border}`,
            borderRadius: 1.5,
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 28, color: red, mr: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: red }}>
              Instruction
            </Typography>
          </Box>
          <HtmlRenderer
            content={instructions}
            component="div"
            sx={{
              lineHeight: 1.5,
              userSelect: "text", // Enable text selection
              cursor: "text", // Show text cursor
              fontSize: "0.875rem", // Match Typography body2
            }}
          />
        </Paper>
      )}
      {Boolean(sectionTitle) && (
        <Typography variant="h6" sx={{ fontWeight: 700, color: blue, mb: 2 }}>
          {sectionTitle}
        </Typography>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `1px solid ${border}`,
          borderRadius: 1.5,
          userSelect: "text", // Enable text selection
          "& *": { userSelect: "text" }, // Ensure all children are selectable
        }}
      >
        <Box
          sx={{
            fontWeight: 500,
            marginBottom: "16px",
            fontSize: "1rem",
            lineHeight: 1.6,
            userSelect: "text", // Enable text selection
            cursor: "text", // Show text cursor
            color: "black", // Keep question text black
            display: "flex",
            alignItems: "flex-start",
            gap: 0.5,
          }}
          onMouseUp={(e) => {
            console.log("Question text mouseup:", e.target.textContent);
          }}
        >
          <Box
            component="span"
            sx={{
              marginRight: "4px",
              userSelect: "text",
              color: "#9D2235",
              flexShrink: 0,
            }}
          >
            {displayNumber}.
          </Box>
          <HtmlRenderer
            content={question}
            component="span"
            sx={{
              flex: 1,
              userSelect: "text",
              cursor: "text",
              color: "black",
            }}
          />
        </Box>

        {/* DROPDOWN MODE */}
        {asDropdown ? (
          <FormControl fullWidth>
            {console.log(
              "Rendering dropdown. isMobile:",
              isMobile,
              "asDropdown:",
              asDropdown
            ) || null}
            {isMobile ? (
              // Native HTML select for mobile - much more reliable
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  bgcolor: "#f9f9f9",
                  border: "1px solid #ccc",
                  "&:hover": {
                    borderColor: "#9D2235",
                  },
                  "&:focus-within": {
                    borderColor: "#9D2235",
                    borderWidth: "2px",
                  },
                  // Critical for iOS Safari interaction
                  isolation: "isolate",
                  zIndex: 1,
                }}
              >
                <select
                  value={selectedValue}
                  onChange={(e) => {
                    console.log("Native select changed:", e.target.value);
                    handleAnswerChange?.(id, e.target.value);
                  }}
                  onFocus={() => {
                    console.log("Native select focused");
                  }}
                  onTouchStart={(e) => {
                    console.log("Native select touch start");
                    // Prevent any interference from parent elements
                    e.stopPropagation();
                  }}
                  onTouchEnd={(e) => {
                    console.log("Native select touch end");
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    console.log("Native select clicked");
                    e.stopPropagation();
                  }}
                  style={{
                    width: "100%",
                    height: "56px", // Fixed height for better touch target
                    padding: "16px 14px",
                    fontSize: "16px", // Prevent zoom on iOS
                    border: "none",
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    outline: "none",
                    cursor: "pointer",
                    fontFamily: theme.typography.fontFamily,
                    // Critical iOS Safari fixes
                    WebkitAppearance: "menulist",
                    appearance: "menulist",
                    position: "relative",
                    zIndex: 10,
                    // Ensure proper interaction
                    pointerEvents: "auto",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "rgba(0,0,0,0)",
                    // Remove any potential blocking styles
                    transform: "translateZ(0)", // Force hardware acceleration
                  }}
                  aria-label="Select answer option"
                  data-testid={`dropdown-mcq-${id}`}
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  {options.map((opt, idx) => {
                    const value = optionValue(opt, idx);
                    return (
                      <option key={idx} value={value}>
                        {`${letterOfIdx(idx)}. ${optionText(opt)}`}
                      </option>
                    );
                  })}
                </select>
              </Box>
            ) : (
              // Material-UI Select for desktop
              <Select
                displayEmpty
                value={selectedValue}
                onChange={(e) => handleAnswerChange?.(id, e.target.value)}
                onTouchStart={(e) => {
                  // Prevent any touch interference
                  e.stopPropagation();
                }}
                onTouchEnd={(e) => {
                  // Ensure dropdown opens on mobile
                  e.stopPropagation();
                }}
                sx={{
                  borderRadius: 2,
                  bgcolor: "#f9f9f9",
                  // Mobile-specific improvements
                  minHeight: 48, // Adequate touch target
                  "& .MuiSelect-select": {
                    minHeight: "1.2rem",
                    padding: "16px 14px",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#9D2235",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#9D2235",
                    },
                  },
                  // Improve touch responsiveness
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
                MenuProps={{
                  disableScrollLock: true,
                  // Fix mobile dropdown menu selection
                  disablePortal: !dropdownContainer, // Use portal only if no container provided
                  container: dropdownContainer, // Render in zoomed container
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      // Ensure proper z-index for mobile
                      zIndex: 1300,
                      "& .MuiMenuItem-root": {
                        minHeight: 48, // Adequate touch targets for menu items
                        padding: "12px 16px",
                        // Fix mobile touch selection
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        WebkitTouchCallout: "none",
                        WebkitTapHighlightColor: "transparent",
                        touchAction: "manipulation",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#9D2235",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "#7a182a",
                          },
                        },
                        // Ensure clicks work on mobile
                        "&:active": {
                          backgroundColor: "#e0e0e0",
                        },
                      },
                    },
                  },
                  // Improve mobile dropdown positioning
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  // Additional mobile-specific props
                  keepMounted: false, // Don't keep mounted to avoid conflicts
                  autoFocus: false, // Prevent auto focus issues on mobile
                }}
                renderValue={(val) => {
                  if (!val) return "Select an option";
                  // Find the option that matches the selected value
                  const selectedOption = options.find((opt, idx) => {
                    const value = optionValue(opt, idx);
                    return value === val;
                  });
                  if (selectedOption) {
                    const idx = options.indexOf(selectedOption);
                    return `${letterOfIdx(idx)}. ${optionText(selectedOption)}`;
                  }
                  return val;
                }}
                inputProps={{
                  "aria-label": "Select answer option",
                  "data-testid": `dropdown-mcq-${id}`,
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    minHeight: 48,
                    padding: "12px 16px",
                    fontStyle: "italic",
                    color: "#666",
                  }}
                >
                  <em>Select an option</em>
                </MenuItem>
                {options.map((opt, idx) => {
                  const value = optionValue(opt, idx);
                  return (
                    <MenuItem
                      key={idx}
                      value={value}
                      onClick={(e) => {
                        // Explicit click handler for mobile
                        e.preventDefault();
                        e.stopPropagation();
                        console.log("MenuItem clicked:", value);
                        handleAnswerChange?.(id, value);
                      }}
                      onTouchEnd={(e) => {
                        // Ensure menu item selection works on mobile
                        e.preventDefault();
                        e.stopPropagation();
                        console.log("MenuItem touched:", value);
                        handleAnswerChange?.(id, value);
                      }}
                      sx={{
                        minHeight: 48,
                        padding: "12px 16px",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        // Critical mobile touch fixes
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        WebkitTouchCallout: "none",
                        WebkitTapHighlightColor: "transparent",
                        touchAction: "manipulation",
                        cursor: "pointer",
                        // Ensure proper touch target
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <span
                        style={{
                          pointerEvents: "none",
                          width: "100%",
                          display: "block",
                          userSelect: "none",
                        }}
                      >
                        {`${letterOfIdx(idx)}. ${optionText(opt)}`}
                      </span>
                    </MenuItem>
                  );
                })}
              </Select>
            )}
          </FormControl>
        ) : (
          true ? (
            // ALL VIEWS: only the circle is clickable. Row body stays selectable
            // so the user can highlight the option text without accidentally
            // selecting/deselecting the answer. Tooltip prompts the user when
            // they tap the row body instead of the circle.
            <Box
              component="div"
              role="radiogroup"
              aria-label={`mcq-${id}`}
              sx={{ width: "100%" }}
            >
              {options.map((opt, idx) => {
                const value = optionValue(opt, idx);
                const letter = value.toUpperCase();
                const chosen = value === selectedValue;
                const selectOption = () => handleAnswerChange?.(id, value);
                return (
                  <Box
                    key={idx}
                    data-testid="mcq-option"
                    onClick={(e) => {
                      // If the click didn't land on the circle, prompt the user
                      // to tap the circle. The circle stops propagation itself.
                      if (!e.defaultPrevented) flashHint(idx);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      mb: 1.5,
                      borderRadius: 2,
                      bgcolor: chosen ? "rgba(10,175,54,0.08)" : "#fff",
                      border: chosen
                        ? `2px solid ${green}`
                        : `2px solid #e6e8ec`,
                      boxShadow: chosen
                        ? "0 4px 12px -4px rgba(10,175,54,0.35)"
                        : "0 1px 2px rgba(0,0,0,0.04)",
                      minHeight: 56,
                      // Row is NOT interactive; only the circle inside is.
                      userSelect: "text",
                      transition:
                        "background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
                    }}
                  >
                    {/* Circle — the only tap target. Tooltip prompts the user
                        when they tap the row body instead of the circle. */}
                    <Tooltip
                      open={hintIdx === idx}
                      title={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            py: 0.25,
                          }}
                        >
                          <TouchAppIcon sx={{ fontSize: 18 }} />
                          <span>Tap the circle to select</span>
                        </Box>
                      }
                      placement="right"
                      arrow
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      PopperProps={{
                        modifiers: [
                          { name: "offset", options: { offset: [0, 4] } },
                        ],
                      }}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: green,
                            color: "#fff",
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 1.5,
                            boxShadow: "0 6px 20px -6px rgba(10,175,54,0.45)",
                          },
                        },
                        arrow: { sx: { color: green } },
                      }}
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          selectOption();
                        }}
                        role="radio"
                        aria-checked={chosen}
                        aria-label={`Select option ${letter}`}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            selectOption();
                          }
                        }}
                        sx={{
                          position: "relative",
                          width: 44,
                          height: 44,
                          minWidth: 44,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: "1.05rem",
                          color: chosen ? "#fff" : green,
                          bgcolor: chosen ? green : "#fff",
                          border: `2.5px solid ${green}`,
                          boxShadow:
                            hintIdx === idx
                              ? `0 0 0 6px rgba(10,175,54,0.28)`
                              : chosen
                                ? `0 0 0 4px rgba(10,175,54,0.18)`
                                : "0 1px 2px rgba(0,0,0,0.06)",
                          cursor: "pointer",
                          flexShrink: 0,
                          touchAction: "manipulation",
                          WebkitTapHighlightColor: "transparent",
                          userSelect: "none",
                          transition:
                            "background-color 0.15s ease, box-shadow 0.2s ease, transform 0.1s ease",
                          animation:
                            hintIdx === idx
                              ? "mcqPulse 0.9s ease-in-out infinite"
                              : "none",
                          "@keyframes mcqPulse": {
                            "0%, 100%": { transform: "scale(1)" },
                            "50%": { transform: "scale(1.08)" },
                          },
                          "&:active": { transform: "scale(0.9)" },
                          "&:focus-visible": {
                            outline: `3px solid ${green}`,
                            outlineOffset: 3,
                          },
                        }}
                      >
                        {chosen ? (
                          <CheckIcon sx={{ fontSize: 24, color: "#fff" }} />
                        ) : (
                          letter
                        )}
                      </Box>
                    </Tooltip>
                    <HtmlRenderer
                      content={optionText(opt)}
                      component="span"
                      sx={{
                        fontWeight: chosen ? 700 : 500,
                        lineHeight: 1.5,
                        flex: 1,
                        color: chosen ? "#065f22" : "#1e293b",
                        userSelect: "text",
                        cursor: "text",
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          ) : (
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                name={`mcq-${id}`}
                value={selectedValue}
                onChange={(e) => handleAnswerChange?.(id, e.target.value)}
                sx={{
                  userSelect: "text",
                  "& .MuiFormGroup-root": { userSelect: "text" },
                }}
              >
                {options.map((opt, idx) => {
                  const value = optionValue(opt, idx);
                  const letter = value.toUpperCase();
                  const chosen = value === selectedValue;
                  return (
                    <FormControlLabel
                      key={idx}
                      value={value}
                      sx={{
                        display: "block",
                        m: 0,
                        mb: 1.5,
                        userSelect: "text",
                        "& .MuiFormControlLabel-label": { userSelect: "text" },
                      }}
                      control={<Radio sx={{ display: "none" }} />}
                      label={
                        <Paper
                          elevation={0}
                          data-testid="mcq-option"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAnswerChange?.(id, value);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 1.5,
                            pl: 2,
                            borderRadius: 1,
                            bgcolor: greyBg,
                            border: chosen
                              ? `2px solid ${green}`
                              : `1px solid transparent`,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#eef0f3" },
                            userSelect: "text",
                            "& *": { userSelect: "text" },
                            pointerEvents: "auto",
                            minHeight: 48,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              minWidth: 32,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 2,
                              fontWeight: 700,
                              color: chosen ? "#fff" : "text.primary",
                              bgcolor: chosen ? green : "#fff",
                              border: `2px solid ${green}`,
                              userSelect: "none",
                              flexShrink: 0,
                            }}
                          >
                            {chosen ? (
                              <CheckIcon sx={{ fontSize: 18, color: "#fff" }} />
                            ) : (
                              letter
                            )}
                          </Box>
                          <HtmlRenderer
                            content={optionText(opt)}
                            component="span"
                            sx={{
                              fontWeight: 500,
                              lineHeight: 1.5,
                              userSelect: "text",
                              cursor: "text",
                              flex: 1,
                              pointerEvents: "none",
                            }}
                          />
                        </Paper>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          )
        )}
      </Paper>
    </Box>
  );
};

export default MockMcq;
